import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, useMap, Tooltip, Circle, FeatureGroup } from "react-leaflet";
import { Button, CircularProgress, Grid, TextField, Paper, Typography, Chip, Alert, IconButton } from "@mui/material";
import './LiveTracker.css';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { format, parseISO, isAfter, isBefore, set } from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faClock,
    faCheckCircle,
    faTimesCircle,
    faMapMarkerAlt,
    faInfoCircle,
    faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { getGeofences, getShiftsByLocation } from "../../../api/apiCall";
import { useAuth } from "../../../auth/auth";

const MapController = ({ coordinates, zoom }: { coordinates: [number, number], zoom: number }) => {
    const map = useMap();
    useEffect(() => {
        if (coordinates) {
            map.setView(coordinates, zoom);
        }
    }, [coordinates, map]);
    return null;
};

const LiveTracker = () => {
    const today = new Date().toISOString().split("T")[0];
    const {state} = useAuth();
    const [shifts, setShifts] = useState<any>([]);
    const [filteredShifts, setFilteredShifts] = useState<any[]>(shifts || []);
    const [selectedShift, setSelectedShift] = useState<any>(null);
    const [geofences, setGeofences] = useState<any>();
    const [coordinates, setCoordinates] = useState<[number, number]>([51.505, -0.09]);
    const [zoom, setZoom] = useState<number>(8);
    const [loading, setLoading] = useState(false);
    const [showAttendance, setShowAttendance] = useState(false);
    const [activeTab, setActiveTab] = useState<'attended' | 'notAttended' | 'late' | 'early'>('attended');
    const [postCodeFilter, setPostCodeFilter] = useState('');
    const [startDateFilter, setStartDateFilter] = useState<any>(today);
    const [endDateFilter, setEndDateFilter] = useState(today);

    const getShiftStatus = (shift: any) => {
        const now = new Date();

        const jobDate = parseISO(shift.jobDate);
        const startTime = parseISO(shift.startTime);
        const endTime = parseISO(shift.endTime);

        const start = set(jobDate, {
            hours: startTime.getHours(),
            minutes: startTime.getMinutes(),
            seconds: 0,
            milliseconds: 0
        });

        const end = set(jobDate, {
            hours: endTime.getHours(),
            minutes: endTime.getMinutes(),
            seconds: 0,
            milliseconds: 0
        });

        if (isBefore(now, start)) return "scheduled";
        if (isAfter(now, end)) return "completed";
        return "ongoing";
    };

    const fetchShifts = async () => {
        setLoading(true);
        try {
            const response = await getShiftsByLocation(state.user?.token, {
                postCode: postCodeFilter,
                startDate: startDateFilter,
                endDate: endDateFilter,
            });

            const shiftsWithStatus = response.data.map((shift: any) => ({
                ...shift,
                status: getShiftStatus(shift),
                color: getShiftStatusColor(getShiftStatus(shift))
            }));
            setShifts(shiftsWithStatus);
            setFilteredShifts(shiftsWithStatus);
        } catch (error) {
            console.error("Error fetching shifts:", error);
        } finally {
            setLoading(false);
        }
    }

    const getGeoFences = async () => {
        try {
            const response = await getGeofences(state.user?.token);
            setGeofences(response?.data || []);
        } catch (error) {
            console.error("Error fetching geofences", error);
        }
    };

    const handleFilter = () => {
        setLoading(true);
        fetchShifts();
        setTimeout(() => {
            setLoading(false);
        }, 500);
    };

    const handleShiftClick = (shift: any) => {
        setSelectedShift(shift);
        if(shift.geofenceId) {
            setCoordinates([shift.latitude, shift.longitude]);
            setZoom(13);
        }
        setShowAttendance(true);
    };

    const getShiftStatusColor = (status: string) => {
        switch(status) {
            case 'scheduled': return '#FFA500';
            case 'ongoing': return '#4CAF50';
            case 'completed': return '#2196F3';
            case 'cancelled': return '#f44336';
            default: return '#9E9E9E';
        }
    };

    const getShiftStatusIcon = (status: string) => {
        switch(status) {
            case 'scheduled': return faClock;
            case 'ongoing': return faCheckCircle;
            case 'completed': return faCheckCircle;
            case 'cancelled': return faTimesCircle;
            default: return faInfoCircle;
        }
    };

    const getAttendanceRate = (shift: any) => {
        return shift.totalWorkers > 0 
            ? ((shift.workersAttended / shift.totalWorkers) * 100).toFixed(1)
            : 0;
    };

    useEffect(() => {
        fetchShifts();
        getGeoFences();
    }, []);
    
    return (
        <div className="shift-tracker-container">
            <Grid className="filters-paper">
                <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 12, md: 3 }}>
                        <TextField
                            label="Post Code"
                            type="text"
                            size="small"
                            value={postCodeFilter}
                            onChange={(e) => setPostCodeFilter(e.target.value)}
                            fullWidth
                            placeholder="Enter postcode..."
                            InputProps={{
                                startAdornment: <FontAwesomeIcon icon={faMapMarkerAlt} style={{ marginRight: 8, color: '#999' }} />
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 2.5 }}>
                        <TextField
                            label="Start Date"
                            type="date"
                            size="small"
                            value={startDateFilter}
                            onChange={(e) => setStartDateFilter(e.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                        <Grid size={{ xs: 12, md: 2.5 }}>
                        <TextField
                            label="End Date"
                            type="date"
                            size="small"
                            value={endDateFilter}
                            onChange={(e) => setEndDateFilter(e.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                 
                    <Grid size={{ xs: 12, md: 2 }}>
                        <Button 
                            onClick={handleFilter} 
                            disabled={loading} 
                            variant="contained" 
                            fullWidth
                            style={{ boxShadow: 'none', height: '40px' }}
                            // startIcon={<FontAwesomeIcon icon={faSearch} />}
                        >
                            {loading ? <CircularProgress size={20} style={{ color: 'white' }} /> : 'Apply Filters'}
                        </Button>
                    </Grid>
                </Grid>
            </Grid>

            <div className="main-content">
                <div className="map-section">
                    <MapContainer center={coordinates} zoom={9} style={{height: '92vh', width: '100%', zIndex: 1}}>
                        <FeatureGroup>
                            {geofences && geofences.map((geofence: any) => (
                                <>
                                    {/* {geofence.type === 'polygon' &&
                                        <Polygon
                                            positions={geofence.vertices}
                                            pathOptions={{ color: geofence.color}}
                                            eventHandlers={{
                                            //   click: () => handleGeofenceClick(geofence),
                                            }}>
                                            <Tooltip permanent={true} direction="top">
                                              <div>{geofence.identifier}</div>
                                            </Tooltip>
                                        </Polygon>
                                    } */}
                                    {geofence.type === 'circle' &&
                                        <Circle
                                            center={[geofence.latitude, geofence.longitude]}
                                            radius={200}
                                            pathOptions={{color: geofence.color}}
                                            eventHandlers={{
                                            //   click: () => handleGeofenceClick(geofence),
                                            }}
                                        >
                                            <Tooltip permanent={true} direction="top">
                                              <div>{geofence.identifier}</div>
                                            </Tooltip>
                                        </Circle>
                                    }
                                </>
                            ))}
                        </FeatureGroup>
                        <TileLayer url="https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}" />
                        <MapController coordinates={coordinates} zoom={zoom}/>
                    </MapContainer>
                </div>

                <div className="shifts-section">
                    <div className="shifts-header">
                        <Typography variant="h6" className="shifts-title">
                            Active Shifts ({filteredShifts.length})
                        </Typography>
                        {filteredShifts.length > 0 && (
                            <Typography variant="caption" className="shifts-hint">
                                Click on any shift to view details
                            </Typography>
                        )}
                    </div>

                    <div className="shifts-list">
                        {filteredShifts.map((shift: any) => (
                            <Grid 
                                key={shift.id} 
                                className={`shift-card ${selectedShift?.id === shift.id ? 'selected' : ''}`}
                                onClick={() => handleShiftClick(shift)}
                            >
                                <div className="shift-header">
                                    <div className="shift-title-section">
                                        <Typography variant="subtitle1" className="shift-name">
                                            {shift.whatsAppName}
                                        </Typography>
                                        <Chip 
                                            label={shift.status.toUpperCase()} 
                                            size="small"
                                            icon={<FontAwesomeIcon color="white" icon={getShiftStatusIcon(shift.status)} size="2xs" />}
                                            style={{ 
                                                backgroundColor: getShiftStatusColor(shift.status),
                                                color: 'white',
                                                fontSize: '11px'
                                            }}
                                        />
                                    </div>
                                    <Typography variant="caption" className="shift-time">
                                        {format(parseISO(shift.startTime), 'HH:mm')} - {format(parseISO(shift.endTime), 'HH:mm')}
                                    </Typography>
                                </div>
                                
                                <div className="shift-details">
                                    <div className="shift-location">
                                        <FontAwesomeIcon icon={faMapMarkerAlt} style={{ fontSize: '12px', marginRight: '4px', color: '#F69E5E' }} />
                                        <Typography variant="body2" color="textSecondary">
                                            {shift.postCode}
                                        </Typography>
                                    </div>
                                </div>
                                
                                <div className="shift-progress">
                                    <div className="progress-label">
                                        <Typography variant="caption">Attendance Rate</Typography>
                                        <Typography variant="caption" fontWeight="bold">{getAttendanceRate(shift)}%</Typography>
                                    </div>
                                    <div className="progress-bar">
                                        <div 
                                            className="progress-fill" 
                                            style={{ width: `${getAttendanceRate(shift)}%` }}
                                        />
                                    </div>
                                </div>
                            </Grid>
                        ))}
                        
                        {filteredShifts.length === 0 && (
                            <Alert severity="info" className="no-shifts-alert" icon={<FontAwesomeIcon icon={faInfoCircle} />}>
                                No shifts found for the selected criteria. Try adjusting your filters.
                            </Alert>
                        )}
                    </div>
                </div>
            </div>

            {showAttendance && selectedShift && (
                <div className="attendance-drawer">
                    <Paper className="attendance-paper">
                        <div className="attendance-header">
                            <div>
                                <Typography variant="h6" style={{color: "black"}} fontWeight="bold">
                                    {selectedShift.whatsAppName}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    {format(parseISO(selectedShift.jobDate), 'EEEE, MMMM d, yyyy')}
                                </Typography>
                            </div>
                            <IconButton
                                size="small" 
                                onClick={() => setShowAttendance(false)}
                                className="close-button"
                            >
                                <FontAwesomeIcon icon={faChevronRight} />
                            </IconButton>
                        </div>

                        <div className="attendance-summary">
                            <div className={`summary-item ${activeTab === 'attended' ? 'active' : ''}`} onClick={() => setActiveTab('attended')}>
                                <CheckCircleIcon style={{ color: '#4CAF50' }} />
                                <span>{selectedShift.workersAttended} All</span>
                            </div>
                        </div>

                        <div className="attendance-list">
                            {activeTab === 'attended' && selectedShift.workers.map((worker: any, idx: any) => (
                                <div key={idx} className="attendance-item">
                                    <div className="worker-info">
                                        <span className="worker-name">{worker.fullName}</span>
                                        <small className="worker-detail">{worker.pts}</small>
                                        {worker.checkInTime && (
                                            <Chip label={`Check-in: ${worker.checkInTime}`} size="small" className="checkin-chip" />
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Paper>
                </div>
            )}
        </div>
    );
};

export default LiveTracker;
